import { isDefined } from "@clipboard-health/util-ts";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, IconButton, Stack } from "@mui/material";
import { Image } from "@src/lib/ui-react";
import { useEffect, useRef, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

interface Props {
  imageUrl: string;
  imageTitle?: string;
  showControls?: boolean;
}

export function ImageDisplay(props: Props) {
  const { imageUrl, imageTitle = "Preview", showControls = true } = props;
  const [rotation, setRotation] = useState(0);
  const resetTransformRef = useRef<(() => void) | undefined>(undefined);
  const rotateClockwise = () => {
    setRotation((previousRotation) => previousRotation + 90);
  };

  const rotateCounterClockwise = () => {
    setRotation((previousRotation) => previousRotation - 90);
  };

  useEffect(() => {
    setRotation(0);
    if (isDefined(resetTransformRef.current)) {
      resetTransformRef.current();
    }
  }, [imageUrl]);

  return (
    <TransformWrapper minScale={0.1} wheel={{ disabled: true }}>
      {({ zoomIn, zoomOut, resetTransform }) => {
        resetTransformRef.current = resetTransform;
        return (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {showControls && (
              <Stack direction="row" spacing={1} justifyContent="center">
                <IconButton
                  aria-label="rotate right"
                  sx={{
                    color: (theme) => theme.palette.grey[700],
                  }}
                  onClick={() => {
                    rotateClockwise();
                  }}
                >
                  <RotateRightIcon />
                </IconButton>
                <IconButton
                  aria-label="rotate left"
                  sx={{
                    color: (theme) => theme.palette.grey[700],
                  }}
                  onClick={() => {
                    rotateCounterClockwise();
                  }}
                >
                  <RotateLeftIcon />
                </IconButton>
                <IconButton
                  aria-label="zoom in"
                  sx={{
                    color: (theme) => theme.palette.grey[700],
                  }}
                  onClick={() => {
                    zoomIn();
                  }}
                >
                  <ZoomInIcon />
                </IconButton>

                <IconButton
                  aria-label="zoom out"
                  sx={{
                    color: (theme) => theme.palette.grey[700],
                  }}
                  onClick={() => {
                    zoomOut();
                  }}
                >
                  <ZoomOutIcon />
                </IconButton>
              </Stack>
            )}
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <TransformComponent>
                <Image
                  sx={{
                    objectFit: "contain",
                    maxWidth: "100%",
                    height: "auto",
                    transform: `rotate(${rotation}deg)`,
                    transition: "transform 0.3s ease",
                  }}
                  title={imageTitle}
                  src={imageUrl}
                  alt={imageTitle}
                />
              </TransformComponent>
            </Box>
          </Box>
        );
      }}
    </TransformWrapper>
  );
}
